import { useCallback, useMemo } from 'react';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import StarSolid from '@simplywallst/ui-core/icons/StarSolid';
import type { ButtonRenderProps } from '@/components/Dropdown';
import { Dropdown } from '@/components/Dropdown';
import { useMetricFullLabel } from '../../hooks/useLabel';
import type { PreferredMultiple } from '../../selector';
import { DropdownButton } from './styled';

interface Props {
  id?: string;
  disabled?: boolean;
  preferredMultiple: PreferredMultiple;
  multiple: PreferredMultiple;
  onSelect: (multiple: PreferredMultiple) => void;
}

const MultipleDropdown = ({
  id,
  disabled,
  preferredMultiple,
  multiple,
  onSelect,
}: Props) => {
  const handleSelect = useCallback(
    (multiple: string) => onSelect(multiple as PreferredMultiple),
    [onSelect]
  );

  const button = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
    }: ButtonRenderProps) => {
      const isPreferredMultiple = preferredMultiple === multiple;
      return (
        <DropdownButton
          styleType="passive"
          alignIcon="right"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          disabled={disabled}
          data-cy-id={name}
          hasIcon={isPreferredMultiple}
        >
          {isPreferredMultiple && <StarSolid />}
          <span>{value}</span>
          <ChevronDownSmallSolid />
        </DropdownButton>
      );
    },
    [disabled, preferredMultiple, multiple]
  );

  const getMetricFullLabel = useMetricFullLabel();

  const options = useMemo(
    () => [
      {
        option: 'PE' as const,
        label: getMetricFullLabel('PE'),
      },
      {
        option: 'PS' as const,
        label: getMetricFullLabel('PS'),
      },
      {
        option: 'PB' as const,
        label: getMetricFullLabel('PB'),
      },
    ],
    [getMetricFullLabel]
  );

  return (
    <Dropdown
      name={`${id}-multiple-selection`}
      align="right"
      displayType="inline-block"
      options={options}
      value={multiple}
      onSelect={handleSelect}
      buttonRender={button}
      showModalSm={false}
    />
  );
};

export default MultipleDropdown;
